import type { FormatOptionLabelMeta } from "react-select";
import styled from "styled-components/macro";
import type { OptionType } from "../../types/types";
import { normalizeSearchText } from "../../util/util";
import { TextXsRegular } from "../Typography/TypographyV2";

/**
 * `label` must stay unique within a dropdown (SelectBoxV2 marks the selected
 * row by comparing labels), so it carries the combined text while
 * `primaryText`/`supportingText` drive what is rendered.
 */
export type TwoLineOption<T = string> = OptionType<T> & {
  primaryText?: string;
  supportingText?: string;
  /** Extra text the option can be found by, see `filterOptionBySearchText`. */
  searchText?: string;
};

const OptionLines = styled.div`
  display: flex;
  flex-direction: column;
  min-width: 0;
`;

const Line = styled.span`
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const SupportingLine = styled(TextXsRegular)`
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

/**
 * `formatOptionLabel` for react-select: two lines in the menu, only the
 * primary line in the selected value.
 */
export const formatTwoLineOptionLabel = <T,>(
  option: TwoLineOption<T>,
  { context }: FormatOptionLabelMeta<TwoLineOption<T>, false>
) => {
  const primaryText = option.primaryText ?? option.label;
  if (context === "value") {
    return primaryText;
  }
  return (
    <OptionLines>
      <Line title={primaryText}>{primaryText}</Line>
      {option.supportingText && (
        <SupportingLine color="secondary" title={option.supportingText}>
          {option.supportingText}
        </SupportingLine>
      )}
    </OptionLines>
  );
};

/**
 * `filterOption` for react-select that matches against the option's
 * `searchText` (falling back to its label) instead of the label alone.
 */
export const filterOptionBySearchText = (
  option: { label: string; data: TwoLineOption<unknown> },
  rawInput: string
) =>
  normalizeSearchText(option.data.searchText ?? option.label).includes(
    normalizeSearchText(rawInput).trim()
  );
